import axios, { AxiosError } from "axios";

import { HW_API_URL } from "../../configs";

/**
 * Promo-code validation against the public (unauthenticated) pricing endpoint
 * `POST /v1/booking/promos/validate` (CID-7209).
 *
 * This is the booking-customer counterpart to the ABT's authenticated
 * `useCheckPromoCode`. The endpoint is lenient: only `promoCode` + `locationId`
 * are required, and any criteria we don't yet know (service date, num hunks) are
 * simply skipped — so we validate with whatever we have so far. It is served by
 * the NestJS pricing API at HW_API_URL, NOT the PHP `fetcher`, so we issue a raw
 * (camelCase, un-snake-cased) axios request here.
 */
export type ValidatePromoCodeParams = {
  promoCode: string;
  locationId: number;
  jobCategoryId?: number;
  /** 2-letter origin state code, when known. */
  state?: string;
  /** Scheduled service date (YYYY-MM-DD), when known (e.g. at booking time). */
  serviceDate?: string;
  /**
   * The date the quote/appointment is being created (YYYY-MM-DD), in the
   * franchise location's own timezone — build it with `todayInLocationZone`.
   * Checked against the promo's availability window. Always send it: when
   * omitted, the backend defaults to the server's UTC date, which around
   * midnight is a day off from the customer's.
   */
  creationDate?: string;
  /**
   * Origin dwelling type id, when known (e.g. Moving quote validation).
   * Always a number — the quote store keeps dwellingType.id numeric (see
   * field-binding-registry.ts). Still normalized defensively below: sessions
   * persisted before the number-in-store convention may replay a string.
   */
  dwellingTypeId?: number;
  /**
   * Number of bedrooms, when known — send the plain count (e.g. 4 for "4+"),
   * same as MET. The backend's own rule-matching already expands
   * numberOfBedrooms >= 4 to also match rules tagged 4.99 ("4 or more") —
   * never send 4.99 from the client.
   */
  numBedrooms?: number;
  /** Crew size (hunks), when known. */
  numHunks?: number;
};

export type PromoValidationResult = {
  valid: boolean;
  /** Present only when invalid — surfaced as the orange helper text. */
  message?: string;
};

export const validatePromoCode = async (
  params: ValidatePromoCodeParams,
): Promise<PromoValidationResult> => {
  // Defensive normalization: guarantee dwellingTypeId goes over the wire as
  // a positive number or not at all. The store keeps it numeric now, but a
  // session persisted before that convention can replay a string — and an
  // empty/non-numeric value would z.coerce to 0/NaN on the backend, fail
  // `.positive()`, and 400 the WHOLE request, which the catch below would
  // misreport as "promo code invalid".
  const parsedDwellingTypeId = Number(params.dwellingTypeId);
  const body = {
    ...params,
    dwellingTypeId:
      Number.isFinite(parsedDwellingTypeId) && parsedDwellingTypeId > 0
        ? parsedDwellingTypeId
        : undefined,
  };

  try {
    const { data } = await axios.post<{ valid: boolean; message?: string }>(
      `${HW_API_URL}/v1/booking/promos/validate`,
      body,
    );
    return { valid: !!data?.valid, message: data?.message };
  } catch (error) {
    const response = (error as AxiosError<{ message?: string }>)?.response;
    // 400 = the code is definitively not valid (doesn't exist / inactive /
    // not eligible for this location). Block and surface the reason.
    if (response?.status === 400) {
      return { valid: false, message: response.data?.message };
    }
    // Network / 5xx — fail open. An infrastructure hiccup must never block a
    // booking: the code is still saved to the lead and re-validated in the ABT.
    return { valid: true };
  }
};
